'use client';

import { type FC, useEffect, useRef, useState } from 'react';
import { BaseLineDown } from '@/app/components/icons/BaseLineDown';
import { BaseLineUp } from '@/app/components/icons/BaseLineUp';
import { ClientLink } from '@/app/components/shared/ClientLink';
import { ImageWithPlaceholder } from '@/app/components/shared/ImageWithPlaceholder';
import { Dialog, DialogContent } from '@/app/components/ui/dialog';
import { QRCODE_URL, SITE } from '@/app/constants';
import { useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { SupportedLanguage } from '@/app/types';
import { cn, getLoginUrl, reportEvent, setRegisterPage } from '@/app/utils';
import { openInNewTab, openOrRedirect, shouldOpenInNewTab } from '@/app/utils/client';
import { useLocale, usePathname, useRouter } from '@/i18n/routing';
import { getMenuConfig } from './utils';

const HomeMenu: FC<{ onClose?: () => void; showMenu: boolean }> = ({ onClose, showMenu }) => {
  const [languageNavOpen, setLanguageNavOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage | null>(null);
  const pathname = usePathname();
  const router = useRouter();
  const locale = useLocale();
  const menuConfig = getMenuConfig();
  const [open, setOpen] = useState(false);
  const userInfo = useStore((state) => state.userInfo);
  const { open: openLoginDialog } = useGlobalModal('loginDialog');
  const hasReportedEarnEntranceExposeRef = useRef(false);

  const handleLanguageChange = (language: SupportedLanguage) => {
    router.replace(pathname, { locale: language });
    setRegisterPage({
      language,
    });
    setLanguageNavOpen(false);
    onClose?.();
  };

  // 初始化选择当前语言
  useEffect(() => {
    if (languageNavOpen) {
      setSelectedLanguage(locale as SupportedLanguage);
    }
  }, [languageNavOpen, locale]);

  useEffect(() => {
    if (showMenu) {
      // 禁用背景滚动
      document.body.style.overflow = 'hidden';
    } else {
      // 恢复背景滚动
      document.body.style.overflow = '';
    }

    // 组件卸载时恢复滚动
    return () => {
      document.body.style.overflow = '';
    };
  }, [showMenu]);

  useEffect(() => {
    const shouldShowEarnEntrance = showMenu && menuConfig.some((item) => item && item.key === 'viduEarn');
    if (!shouldShowEarnEntrance || hasReportedEarnEntranceExposeRef.current) return;

    reportEvent('earn_home_entrance_expose', { from_source: 'official_web' });
    hasReportedEarnEntranceExposeRef.current = true;
  }, [menuConfig, showMenu]);

  return (
    <div className="flex h-screen w-full flex-col justify-start overflow-y-auto p-4 text-[14px] text-white">
      <div className="flex flex-col gap-6">
        {menuConfig.map(
          (item) =>
            item && (
              <div key={item.key}>
                {item.children && item.children.length > 0 && !item?.icon ? (
                  <div>
                    <div className="px-4 py-2 text-[14px] leading-[22px] opacity-40" key={item.key}>
                      {item.label}
                    </div>
                    <div className="flex flex-col">
                      {item.children?.map((child) => {
                        if (!child) return null;
                        const shouldRequireLogin =
                          SITE === 'china' &&
                          (child.key === 'character2video' || child.key === 'img2video' || child.key === 'text2video');

                        return (
                          <ClientLink
                            href={child.href}
                            key={child.key}
                            onClick={(e) => {
                              if (shouldOpenInNewTab(e)) {
                                if (shouldRequireLogin && !userInfo) {
                                  e.preventDefault();
                                  const target = getLoginUrl(child.href);
                                  openInNewTab(target);
                                  reportEvent('login_page_exposure', {
                                    refer_element: `header - ${child.key}`,
                                    refer_page: pathname,
                                  });
                                }
                                return;
                              }
                              e.preventDefault();
                              if (shouldRequireLogin) {
                                if (!userInfo) {
                                  openLoginDialog();
                                  reportEvent('login_pop_exposure', {
                                    refer_element: `header - ${child.key}`,
                                    refer_page: pathname,
                                  });
                                } else {
                                  router.push(child.href);
                                }
                              } else {
                                reportEvent('element_click', {
                                  element_name: child.key,
                                });
                                openOrRedirect(child.href);
                              }
                            }}
                            className="block px-4 text-[16px] leading-[40px]"
                          >
                            {child.label}
                          </ClientLink>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div>
                    {item?.icon ? (
                      <>
                        {/* 中英文切换 */}
                        <div
                          className="flex items-center gap-2 px-4 py-2 text-[16px] leading-[22px]"
                          onClick={() => setLanguageNavOpen(!languageNavOpen)}
                        >
                          {item.label}
                          {languageNavOpen ? <BaseLineUp className="size-5" /> : <BaseLineDown className="size-5" />}
                        </div>
                        {languageNavOpen && (
                          <div className="flex flex-col">
                            {item.children?.map((child) => {
                              if (!child) return null;
                              return (
                                <div
                                  key={child.key}
                                  className={cn(
                                    'px-4 text-[16px] leading-[40px]',
                                    selectedLanguage === child.key && 'bg-system-white12',
                                  )}
                                  onClick={() => handleLanguageChange(child.key as SupportedLanguage)}
                                >
                                  {child.label}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    ) : (
                      <div
                        className="px-4 py-2 text-[16px] leading-[22px]"
                        key={item.key}
                        onClick={() => {
                          // 社区弹窗
                          if (item.key === 'community') {
                            setOpen(true);
                          } else {
                            if (item.href) {
                              reportEvent('element_click', {
                                element_name: `${item.key}`,
                              });
                              if (item.key === 'viduEarn') {
                                reportEvent('earn_home_entrance_click', { from_source: 'official_web' });
                              }
                              openOrRedirect(item.href);
                            }
                          }
                        }}
                      >
                        {item.label}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ),
        )}
      </div>
      <div className="absolute top-7 right-4 flex size-9 cursor-pointer items-center justify-center rounded-full bg-system-white12">
        <BaseLineUp className="size-5" onClick={onClose} />
      </div>
      <Dialog open={open} onOpenChange={() => setOpen(false)}>
        <DialogContent className="w-[254px]" onClose={() => setOpen(false)} disableOverlayClose={true}>
          <div className="pt-[40px]">
            <div className="mx-auto h-[200px] w-[200px]">
              <ImageWithPlaceholder
                src={QRCODE_URL}
                alt="微信群二维码"
                className="h-full w-full rounded-6 bg-[#3C4352]"
              />
            </div>
            <p className="mt-4 text-center font-[400] text-system-white64 text-xs tracking-[0]">
              加入讨论群，了解最新动态
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomeMenu;
